import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faRotateRight,
  faUser,
  faKey,
  faFileContract,
  faCloudArrowUp,
  faDesktop,
  faMobileScreen,
  faTabletScreenButton,
  faTrashCan,
  faPen,
  faCheck,
  faXmark,
  faCircleExclamation,
  faTriangleExclamation,
  faCircleInfo,
  faChevronDown,
  faEnvelope,
  faPhone,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { useDashboardAuth } from '../../context/DashboardAuthContext';
import desktopApi, { describeError } from './desktopApi';
import {
  formatDate,
  formatDateTime,
  timeAgo,
  daysUntil,
  formatBytes,
  formatAmount,
  parseUtc,
  MODE_LABELS,
  PLATFORM_LABELS,
} from './desktopFormat';
import { DeviceGauge, ShopBadges } from './DesktopShops';
import './Desktop.css';

const SEVERITY_ICON = { critical: faCircleExclamation, warning: faTriangleExclamation, info: faCircleInfo };
const PLATFORM_ICON = { windows: faDesktop, android: faMobileScreen, ios: faTabletScreenButton };
const MAX_DEVICES = 200;

const InfoRow = ({ label, children }) => (
  <div className="dk-info-row">
    <span className="dk-info-label">{label}</span>
    <span className="dk-info-value">{children}</span>
  </div>
);

// One shop: who owns it, its licence, contract and backup, and the machines bound to it.
const DesktopShopDetail = () => {
  const { id } = useParams();
  const { hasPermission, isSuperAdmin } = useDashboardAuth();
  const canManageDevices = hasPermission('desktop.devices.manage');
  const canChangeLimit = isSuperAdmin();

  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const [removing, setRemoving] = useState(null); // device being removed
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingLimit, setEditingLimit] = useState(false);
  const [limit, setLimit] = useState('');

  const notify = (type, message) => {
    setNotice({ type, message });
    setTimeout(() => setNotice(null), 5000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setDetail(await desktopApi.shop(id));
      setError(null);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const confirmRemove = async () => {
    setBusy(true);
    try {
      await desktopApi.removeDevice(id, removing.id, reason.trim());
      notify('success', `« ${removing.name || 'Appareil'} » a été retiré. La place est libérée.`);
      setRemoving(null);
      setReason('');
      await load();
    } catch (e) {
      notify('error', describeError(e));
    } finally {
      setBusy(false);
    }
  };

  const saveLimit = async () => {
    const value = Number(limit);
    if (!Number.isInteger(value) || value < 1 || value > MAX_DEVICES) {
      notify('error', `Le nombre d'appareils doit être entre 1 et ${MAX_DEVICES}.`);
      return;
    }
    setBusy(true);
    try {
      await desktopApi.setMaxDevices(id, value);
      notify('success', `Limite fixée à ${value} appareil(s). La boutique l'appliquera à sa prochaine connexion.`);
      setEditingLimit(false);
      await load();
    } catch (e) {
      notify('error', describeError(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading && !detail) {
    return <div className="dk-page"><div className="dk-loading"><div className="spinner"></div><p>Chargement...</p></div></div>;
  }

  if (error && !detail) {
    return (
      <div className="dk-page">
        <Link to="/desktop/shops" className="dk-back"><FontAwesomeIcon icon={faArrowLeft} /> Boutiques</Link>
        <div className="dk-error">
          <FontAwesomeIcon icon={faCircleExclamation} />
          <span>{error}</span>
          <button className="dk-btn" onClick={load}>Réessayer</button>
        </div>
      </div>
    );
  }

  const { shop, owner, memberCount, blockReason, devices, subscription, backup, attention } = detail;
  const active = devices.filter((d) => !d.revokedAt);
  const revoked = devices.filter((d) => d.revokedAt);
  const online = shop.mode === 'online';

  // Offline days left before an online shop's app locks itself.
  const lastCheck = parseUtc(shop.lastLicenceCheckAt);
  const offlineDays = lastCheck ? Math.floor((Date.now() - lastCheck.getTime()) / 86400000) : null;
  const daysLeft = offlineDays === null ? null : shop.maxOfflineDays - offlineDays;
  const subscriptionDays = daysUntil(subscription?.endsAt);

  return (
    <div className="dk-page">
      {notice && <div className={`dk-notice ${notice.type}`}>{notice.message}</div>}

      <Link to="/desktop/shops" className="dk-back"><FontAwesomeIcon icon={faArrowLeft} /> Boutiques</Link>

      <div className="dk-header">
        <div>
          <h1>{shop.name}</h1>
          <p className="dk-badges"><ShopBadges shop={shop} /> <span className="dk-muted">Créée le {formatDate(shop.createdAt)}</span></p>
        </div>
        <div className="dk-header-actions">
          <button className="dk-btn" onClick={load} disabled={loading}>
            <FontAwesomeIcon icon={faRotateRight} spin={loading} /> Actualiser
          </button>
        </div>
      </div>

      {shop.isBlocked && (
        <div className="dk-error"><FontAwesomeIcon icon={faCircleExclamation} /><span>Boutique bloquée{blockReason ? ` : ${blockReason}` : ''}</span></div>
      )}

      {attention.length > 0 && (
        <section className="dk-card">
          <ul className="dk-attention compact">
            {attention.map((item, index) => (
              <li key={`${item.kind}-${index}`} className={`dk-attention-row sev-${item.severity}`}>
                <FontAwesomeIcon icon={SEVERITY_ICON[item.severity]} className="dk-sev-icon" />
                <span className="dk-attention-text"><span>{item.message}</span></span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="dk-grid">
        <section className="dk-card">
          <div className="dk-card-head"><h2><FontAwesomeIcon icon={faUser} /> Propriétaire</h2></div>
          {owner ? (
            <>
              <InfoRow label="Nom">{owner.name || '—'}</InfoRow>
              <InfoRow label="E-mail"><a href={`mailto:${owner.email}`}><FontAwesomeIcon icon={faEnvelope} /> {owner.email}</a></InfoRow>
              <InfoRow label="Téléphone">{owner.phone ? <a href={`tel:${owner.phone}`}><FontAwesomeIcon icon={faPhone} /> {owner.phone}</a> : '—'}</InfoRow>
            </>
          ) : <p className="dk-muted">Propriétaire introuvable.</p>}
          <InfoRow label="Membres"><FontAwesomeIcon icon={faUsers} /> {memberCount}</InfoRow>
        </section>

        <section className="dk-card">
          <div className="dk-card-head"><h2><FontAwesomeIcon icon={faKey} /> Licence</h2></div>
          <InfoRow label="Mode">{MODE_LABELS[shop.mode] || shop.mode}</InfoRow>
          <InfoRow label="Dernière connexion au serveur">
            {lastCheck ? <span title={formatDateTime(shop.lastLicenceCheckAt)}>{timeAgo(shop.lastLicenceCheckAt)}</span> : 'Jamais'}
          </InfoRow>
          {online ? (
            <InfoRow label="Hors ligne autorisé">
              {daysLeft === null ? `${shop.maxOfflineDays} jours`
                : daysLeft < 0 ? <span className="dk-text-danger">Dépassé : application bloquée</span>
                : <span className={daysLeft <= 3 ? 'dk-text-warning' : ''}>{daysLeft} jour(s) restant(s) sur {shop.maxOfflineDays}</span>}
            </InfoRow>
          ) : (
            <InfoRow label="Hors ligne autorisé">Illimité (mode local)</InfoRow>
          )}
          <InfoRow label="Version">{shop.appVersion || '—'}</InfoRow>
          <InfoRow label="Identifiant"><code className="dk-code">{shop.id}</code></InfoRow>
        </section>

        <section className="dk-card">
          <div className="dk-card-head"><h2><FontAwesomeIcon icon={faFileContract} /> Abonnement</h2></div>
          {!online && !subscription ? (
            <p className="dk-muted">Mode local : pas d'abonnement requis.</p>
          ) : subscription ? (
            <>
              <InfoRow label="Statut">
                <span className={subscription.isCurrent ? 'dk-text-success' : 'dk-text-danger'}>
                  {subscription.isCurrent ? 'Actif' : subscription.status}
                </span>
              </InfoRow>
              <InfoRow label="Du">{formatDate(subscription.startsAt)}</InfoRow>
              <InfoRow label="Au">
                {subscription.endsAt ? (
                  <span className={subscriptionDays !== null && subscriptionDays <= 7 ? 'dk-text-warning' : ''}>
                    {formatDate(subscription.endsAt)}{subscriptionDays !== null && subscriptionDays >= 0 ? ` (dans ${subscriptionDays} j)` : ''}
                  </span>
                ) : 'Sans fin'}
              </InfoRow>
              <InfoRow label="Montant">{formatAmount(subscription.amount)}</InfoRow>
            </>
          ) : (
            <p className="dk-text-danger">Mode en ligne sans abonnement.</p>
          )}
        </section>

        <section className="dk-card">
          <div className="dk-card-head"><h2><FontAwesomeIcon icon={faCloudArrowUp} /> Sauvegarde cloud</h2></div>
          {!online ? (
            <p className="dk-muted">Mode local : les données restent sur l'ordinateur de la boutique.</p>
          ) : backup?.exists ? (
            <>
              <InfoRow label="Dernière sauvegarde"><span title={formatDateTime(backup.snapshotAt)}>{timeAgo(backup.snapshotAt)}</span></InfoRow>
              <InfoRow label="Taille">{formatBytes(backup.sizeBytes)}</InfoRow>
              <InfoRow label="Contenu">{backup.recordCount.toLocaleString('fr-FR')} enregistrements · {backup.imageCount} photo(s)</InfoRow>
            </>
          ) : (
            <p className="dk-text-warning">Aucune sauvegarde reçue.</p>
          )}
        </section>
      </div>

      <section className="dk-card">
        <div className="dk-card-head">
          <h2><FontAwesomeIcon icon={faDesktop} /> Appareils</h2>
          <DeviceGauge used={active.length} max={shop.maxDevices} />
          {canChangeLimit && !editingLimit && (
            <button className="dk-btn dk-btn-small" onClick={() => { setLimit(String(shop.maxDevices)); setEditingLimit(true); }}>
              <FontAwesomeIcon icon={faPen} /> Modifier la limite
            </button>
          )}
          {editingLimit && (
            <span className="dk-limit-edit">
              <input type="number" min="1" max={MAX_DEVICES} value={limit} onChange={(e) => setLimit(e.target.value)} autoFocus
                     onKeyDown={(e) => { if (e.key === 'Enter') saveLimit(); if (e.key === 'Escape') setEditingLimit(false); }} />
              <button className="dk-btn dk-btn-primary dk-btn-small" onClick={saveLimit} disabled={busy}><FontAwesomeIcon icon={faCheck} /> Enregistrer</button>
              <button className="dk-btn dk-btn-small" onClick={() => setEditingLimit(false)} disabled={busy}><FontAwesomeIcon icon={faXmark} /></button>
            </span>
          )}
        </div>

        {active.length === 0 ? (
          <p className="dk-muted">Aucun appareil actif.</p>
        ) : (
          <ul className="dk-devices">
            {active.map((d) => (
              <li key={d.id} className="dk-device">
                <span className="dk-device-icon"><FontAwesomeIcon icon={PLATFORM_ICON[d.platform] || faDesktop} /></span>
                <span className="dk-device-main">
                  <strong>{d.name || 'Sans nom'}</strong>
                  <span className="dk-muted">
                    {PLATFORM_LABELS[d.platform] || d.platform}
                    {d.appVersion && ` · v${d.appVersion}`}
                    {d.lastIpAddress && ` · ${d.lastIpAddress}`}
                  </span>
                </span>
                <span className="dk-device-dates">
                  <span title={formatDateTime(d.lastSeenAt)}>Vu {timeAgo(d.lastSeenAt)}</span>
                  <span className="dk-muted">Ajouté le {formatDate(d.registeredAt)}</span>
                </span>
                {canManageDevices && (
                  <button className="dk-btn dk-btn-danger dk-btn-small" onClick={() => { setRemoving(d); setReason(''); }}>
                    <FontAwesomeIcon icon={faTrashCan} /> Retirer
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {revoked.length > 0 && (
          <>
            <button className="dk-link-btn" onClick={() => setShowRevoked((v) => !v)}>
              <FontAwesomeIcon icon={faChevronDown} className={showRevoked ? 'dk-rot' : ''} />
              {showRevoked ? 'Masquer' : 'Afficher'} les appareils retirés ({revoked.length})
            </button>
            {showRevoked && (
              <ul className="dk-devices revoked">
                {revoked.map((d) => (
                  <li key={d.id} className="dk-device">
                    <span className="dk-device-icon"><FontAwesomeIcon icon={PLATFORM_ICON[d.platform] || faDesktop} /></span>
                    <span className="dk-device-main">
                      <strong>{d.name || 'Sans nom'}</strong>
                      <span className="dk-muted">{d.revokedReason || 'Retiré'}</span>
                    </span>
                    <span className="dk-device-dates">
                      <span>Retiré le {formatDate(d.revokedAt)}</span>
                      <span className="dk-muted">Vu {timeAgo(d.lastSeenAt)}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      {removing && (
        <div className="dk-modal-backdrop" onClick={() => !busy && setRemoving(null)}>
          <div className="dk-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h3>Retirer « {removing.name || 'cet appareil'} » ?</h3>
            <p>
              L'appareil ne pourra plus ouvrir {shop.name} et sa place sera libérée.
              S'il revient un jour, un administrateur de la boutique devra l'enregistrer à nouveau.
            </p>
            <label htmlFor="dk-reason">Raison (facultatif)</label>
            <input id="dk-reason" type="text" maxLength={200} placeholder="Ex. : en panne, volé, remplacé"
                   value={reason} onChange={(e) => setReason(e.target.value)} autoFocus
                   onKeyDown={(e) => e.key === 'Enter' && confirmRemove()} />
            <div className="dk-modal-actions">
              <button className="dk-btn" onClick={() => setRemoving(null)} disabled={busy}>Annuler</button>
              <button className="dk-btn dk-btn-danger" onClick={confirmRemove} disabled={busy}>
                <FontAwesomeIcon icon={faTrashCan} /> Retirer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopShopDetail;
